import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Mail, CheckCircle, Loader2 } from 'lucide-react';

import { customerBookingsService } from '@/services/api/customer-bookings.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/form-field';
import { Alert, AlertDescription } from '@/components/ui/alert';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Email form that triggers the "My bookings" magic link. Shared by the
 * standalone request page and the expired-token state of the bookings page,
 * so an expired link always drops the customer straight back onto the form.
 */
export function MagicLinkRequestForm() {
  const { t } = useTranslation('myBookings');
  const [email, setEmail] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: (value: string) => customerBookingsService.requestMagicLink(value),
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim();

    if (!EMAIL_PATTERN.test(trimmed)) {
      setValidationError(t('form.invalidEmail'));
      return;
    }

    setValidationError(null);
    mutate(trimmed);
  };

  if (isSuccess) {
    return (
      <div className="text-center space-y-6 py-6">
        <div className="flex justify-center">
          <div className="rounded-full bg-green-100 p-3">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
        </div>

        <div className="space-y-2">
          <h3 className="text-h3 text-ink">{t('form.emailSent')}</h3>
          <p className="text-soft text-sm">{t('form.emailSentMessage', { email })}</p>
          <p className="text-soft text-sm">{t('form.checkSpam')}</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Alert>
        <AlertDescription className="text-sm">{t('form.hint')}</AlertDescription>
      </Alert>

      {isError && (
        <Alert variant="destructive">
          <AlertDescription className="text-sm">{t('form.requestFailed')}</AlertDescription>
        </Alert>
      )}

      <FormField label={t('form.emailLabel')} error={validationError ?? undefined}>
        <Input
          id="email"
          type="email"
          inputSize="lg"
          autoComplete="email"
          placeholder={t('form.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </FormField>

      <Button type="submit" size="lg" className="w-full" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t('form.sending')}
          </>
        ) : (
          <>
            <Mail className="w-4 h-4 mr-2" />
            {t('form.button')}
          </>
        )}
      </Button>
    </form>
  );
}
